<script setup>
//#region import
import { ref, computed, onBeforeMount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { find, cloneDeep, uniqBy, debounce, pickBy, pick, mapValues, isEmpty } from 'lodash-es';
import hash from 'object-hash';
import { useTypedStore } from '@/store';
import { useApi } from '@/api';
import { useFilterUtils } from '@/composables/useFilterUtils';
import ColorTags from '@/components/ColorTags.vue';
//#endregion

//#region define
const props = defineProps({
  modelValue: {
    type: Object,
    default: () => {},
  },
});

const emit = defineEmits(['submit']);
//#endregion

//#region composables
const { t } = useI18n();
const api = useApi();
const { apiToCustom, customToApi } = useFilterUtils({
  canBeUndefined: [
    'with_tracking_url_blocklist',
    'referral_program_enabled',
    'media_buying_enabled',
    'payments_enabled',
    'payments_negative_carryover',
    'note_tag_id',
  ],
});
const store = useTypedStore();
const availableNoteTags = ['white', 'green', 'red', 'yellow', 'no-color'];
//#endregion

//#region data
const currentFilters = ref({account_status: ['approved']});
const loading = ref(false);
const oldHash = ref(null);
const newHash = ref(null);
const affiliateOptions = ref([]);
//#endregion

//#region computed
const countries = computed(() => store.getters['misc/countries']);
const paymentPeriods = computed(() => store.getters['misc/paymentPeriods']);
const paymentMethods = computed(() => store.getters['misc/paymentMethods']);

const activeList = computed(() => {
  return [
    {
      value: 'undefined',
      label: t('ui.filters.select.all'),
    },
    {
      label: t('ui.filters.select.inactive'),
      value: false,
    },
    {
      label: t('ui.filters.select.active'),
      value: true,
    },
  ];
});
const answerList = computed(() => {
  return [
    {
      value: 'undefined',
      label: t('ui.filters.select.all'),
    },
    {
      label: t('ui.filters.select.no'),
      value: false,
    },
    {
      label: t('ui.filters.select.yes'),
      value: true,
    },
  ];
});
const disableList = computed(() => {
  return [
    {
      value: 'undefined',
      label: t('ui.filters.select.all'),
    },
    {
      label: t('ui.filters.select.disabled'),
      value: false,
    },
    {
      label: t('ui.filters.select.enabled'),
      value: true,
    },
  ];
});
const accountStatusList = computed(() => {
  return [
    {
      label: t('affiliates.listView.toolbar[1].statusFilter.approved'),
      value: 'approved',
    },
    {
      label: t('affiliates.listView.toolbar[1].statusFilter.blocked'),
      value: 'blocked',
    },
    {
      label: t('affiliates.listView.toolbar[1].statusFilter.rejected'),
      value: 'rejected',
    },
  ];
});

const isActiveButton = computed(() => {
  return oldHash.value !== newHash.value;
});

const pPaymentPeriods = computed(() => {
  return paymentPeriods.value.map((el) => ({ id: el, name: el.toUpperCase() }));
});

const placeholderWallet = computed(() => {
  if (paymentMethods.value && paymentMethods.value.length !== 0 && currentFilters.value.site_payment_method_id) {
    return find(
      paymentMethods.value,
      (e) => e.id === currentFilters.value.site_payment_method_id,
    ).wallet_placeholder;
  }
  return '';
});

const sortedPaymentMethods = computed(() => {
  return cloneDeep(paymentMethods.value).sort((x, y) => {
    if (x.id === currentFilters.value.site_payment_method_id) {
      return -1;
    }
    return x.is_disabled - y.is_disabled;
  }).filter((el) => el.name.toLowerCase());
});

const hasFilters = computed(() => {
  const { account_status, ...restFilters } = pickBy(props.modelValue, filterFn);
  return !isEmpty(restFilters) || !(account_status.length === 1 && account_status[0] === 'approved');
});
//#endregion

//#region watch
watch(currentFilters, (newVal) => {
  const data = {
    with_multiaccounts: false,
    ...pickBy(newVal, filterFn),
  };
  newHash.value = hash(data, { algorithm: 'md5' });
}, { deep: true, immediate: true });
//#endregion

//#region methods
function filterFn(v) {
  return v !== undefined && v !== '';
}
function getAffiliates(search) {
  const query = {
    limit: 100,
    offset: 0,
    search,
    account_status: ['approved'],
    referral_program_enabled: true,
  };
  return new Promise((resolve, reject) => {
    api.getAffiliates(query)
      .then((response) => {
        affiliateOptions.value = uniqBy([
          ...(props.modelValue?.referred?.id && !search ?
              [{ id: props.modelValue?.referred?.id, email: props.modelValue?.referred?.email }] : []
          ),
          ...response.data.payload,
        ], 'id');
        resolve(response.data.payload);
      })
      .catch((error) => {
        reject(error);
      })
      .finally(() => {
        loading.value = false;
      });
  });
}

function searchMethod(v) {
  loading.value = true;
  debounce(getAffiliates, 800)(v);
}

function getFilterParams() {
  const {
    referred_by,
    affiliate_referral_id,
    note_tag_id,
    ...restParams
  } = props.modelValue;

  currentFilters.value = cloneDeep({
    account_status: ['approved'],
    note_tag_id: note_tag_id === null ? 'null' : note_tag_id,
    ...restParams,
    ...(referred_by && affiliate_referral_id && {
      referred: {
        referral_id: referred_by,
        id: affiliate_referral_id,
      },
    }),
  });

  const data = {
    with_multiaccounts: false,
    ...pickBy(props.modelValue, filterFn),
  };

  const currentFiltersToCustom = apiToCustom(currentFilters.value);
  const dataToCustom = apiToCustom(data);

  currentFilters.value = currentFiltersToCustom;

  oldHash.value = hash(dataToCustom, { algorithm: 'md5' });
}

function handleApply() {
  currentFilters.value = mapValues(currentFilters.value, (v) => (typeof v === 'string' ? v.trim() : v));
  let {
    payment_wallet,
    site_payment_method_id,
    referred,
    with_multiaccounts,
    note_tag_id,
    note_text,
    promo_code,
    ...restFilters
  } = currentFilters.value;
  if (referred) {
    referred = pick(referred, ['id', 'referral_id', 'email']);
  }
  const params = {
    ...(payment_wallet && { payment_wallet }),
    ...(site_payment_method_id && { site_payment_method_id }),
    ...(with_multiaccounts && { with_multiaccounts }),
    ...(note_text && { note_text }),
    ...(promo_code && { promo_code }),

    ...{ referred },
    ...restFilters,
  };

  if (note_tag_id !== undefined) {
    params.note_tag_id = note_tag_id;
  }

  const result = customToApi(params);

  emit('submit', result);
}

function handleReset() {
  currentFilters.value = {
    account_status: ['approved'],
  };
  emit('submit', currentFilters.value);
}
//#endregion

//#region hook
onBeforeMount(() => {
  getAffiliates();
  getFilterParams();
});
//#endregion
</script>

<template>
  <UiFilterElement
    class="affiliate-filter"
    :is-active-button="isActiveButton"
    :has-filters="hasFilters"
    @open="getFilterParams"
    @apply="handleApply"
    @reset="handleReset"
  >
    <UiFilterItem label="affiliates.filters.status">
      <UiCheckboxGroup
        v-model="currentFilters.account_status"
        :options="accountStatusList"
      />
    </UiFilterItem>
    <UiFilterItem label="affiliates.filters.promoCode">
      <UiInput
        v-model="currentFilters.promo_code"
        :placeholder="$t('affiliates.filters.promoCode')"
        :width="200"
      />
    </UiFilterItem>
    <UiFilterItem label="affiliates.filters.country">
      <UiSelectElement
        v-model="currentFilters.country_code"
        :placeholder="$t('affiliates.filters.country')"
        :list="countries"
        :width="200"
        option-value-param="code"
        filterable
        clearable
      />
    </UiFilterItem>
    <UiFilterItem label="affiliates.filters.onlyMultiaccounts">
      <UiSwitch
        v-model="currentFilters.with_multiaccounts"
      />
    </UiFilterItem>
    <UiFilterItem label="affiliates.filters.introducedBy">
      <UiSelectElement
        ref="introducedByFilter"
        v-model="currentFilters.referred"
        :placeholder="$t('affiliates.filters.introducedBy')"
        :list="affiliateOptions"
        :remote-method="searchMethod"
        width="200"
        is-value-object
        value-key="id"
        remote
        :loading="loading"
        option-name-param="email"
        option-label-param="email"
        filterable
        clearable
      />
    </UiFilterItem>
    <UiFilterItem label="affiliates.filters.trafficBlocklist">
      <UiRadioGroup
        v-model="currentFilters.with_tracking_url_blocklist"
        :options="answerList"
      />
    </UiFilterItem>
    <UiFilterItem label="affiliates.filters.subAffiliatesProgram">
      <UiRadioGroup
        v-model="currentFilters.referral_program_enabled"
        :options="activeList"
      />
    </UiFilterItem>
    <UiFilterItem label="affiliates.filters.mediaBuying">
      <UiRadioGroup
        v-model="currentFilters.media_buying_enabled"
        :options="disableList"
      />
    </UiFilterItem>
    <UiFilterItem label="affiliates.filters.createPayments">
      <UiRadioGroup
        v-model="currentFilters.payments_enabled"
        :options="disableList"
      />
    </UiFilterItem>
    <UiFilterItem label="affiliates.filters.paymentMethod">
      <UiSelectElement
        v-model="currentFilters.site_payment_method_id"
        :placeholder="$t(`affiliates.cardView.info.paymentMethod`)"
        :list="sortedPaymentMethods"
        width="200"
        filterable
        clearable
        ellipsis
      />
    </UiFilterItem>
    <UiFilterItem label="affiliates.filters.wallet">
      <UiInput
        v-model="currentFilters.payment_wallet"
        :disabled="!currentFilters.site_payment_method_id"
        :placeholder="placeholderWallet || ''"
        :width="200"
      />
    </UiFilterItem>
    <UiFilterItem label="affiliates.filters.paymentPeriod">
      <UiSelectElement
        v-model="currentFilters.payments_net"
        :placeholder="$t(`affiliates.cardView.info.paymentPeriod`)"
        :list="pPaymentPeriods"
        clearable
        width="200"
      />
    </UiFilterItem>
    <UiFilterItem label="affiliates.filters.negativeCarryover">
      <UiRadioGroup
        v-model="currentFilters.payments_negative_carryover"
        :options="answerList"
      />
    </UiFilterItem>
    <UiFilterItem label="affiliates.filters.noteColor">
      <ColorTags
        v-model="currentFilters.note_tag_id"
        :available-note-tags="availableNoteTags"
      />
    </UiFilterItem>
    <UiFilterItem label="affiliates.filters.noteText">
      <UiInput
        v-model="currentFilters.note_text"
        :placeholder="$t('affiliates.filters.noteText')"
        :width="200"
      />
    </UiFilterItem>
  </UiFilterElement>
</template>
